import { Box } from '@mui/material';
import { useCallback, useMemo, useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Navigate, useParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import ReportToolbar from './Buttons/ReportToolbar';
import ReportHeader from './ReportHeader/ReportHeader';
import Tables from './Report/Tables';
import Loader from '../../../Components/Loader';
import { reportTypes } from '../../../Enums/Enum';
import { buildMemorizePayload, buildResetPayload } from '../../../Payloads/memorizePayload';
import { buildGeneratePayload } from '../../../Payloads/generatePayload';
import {
  getClientUrlFromUrl,
  getAuthIDFromUrl,
  getMenuIdFromUrl,
  getCorpIdFromUrl,
} from '../../../APIs/auth';
import {
  GetTimeAndAttendanceReport,
  SaveMemorizedTimeAndAttendanceReport,
  ResetMemorizedTimeAndAttendanceReport,
  getDepartments,
  getJobTitles,
} from '../../../api';
import { usePermissions } from '../../../Hooks';
import { generateTimeAndAttendancePdf, generateTimeAndAttendanceExcel } from './Export';



const LabourReportsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { clintUrl, authId, menuId, corpId } = useParams();
  const { enqueueSnackbar } = useSnackbar();

  const finalMenuId = menuId || getMenuIdFromUrl();
  const { permissions, loading: permissionsLoading } = usePermissions(finalMenuId);

  const { filters, reportType, selectedFields, reportData: initialReportData } = location.state || {};
  const [reportData, setReportData] = useState(initialReportData || null);


  const reportTitle = useMemo(() => {
    const report = reportTypes.find((rt) => rt.value === reportType);
    return report ? `Employee Time and Attendance Report - ${report.label}` : 'OTB Report';
  }, [reportType]);

  const getCustomizeNavigationUrl = useCallback(() => {
    const finalClintUrl = clintUrl || getClientUrlFromUrl();
    const finalAuthId = authId || getAuthIDFromUrl();
    let finalCorpId = corpId || getCorpIdFromUrl() || 'ALL';

    if (filters?.selectedCorporationIds && filters.selectedCorporationIds.length > 0) {
      finalCorpId = filters.selectedCorporationIds[0].split('_')[0] || finalCorpId;
    }

    if (finalClintUrl && finalAuthId && finalMenuId) {
      return `/TimeandAttendance/${encodeURIComponent(finalClintUrl)}/${encodeURIComponent(finalAuthId)}/${encodeURIComponent(
        finalMenuId
      )}/${encodeURIComponent(finalCorpId)}`;
    }
    return '/TimeandAttendance';
  }, [clintUrl, authId, finalMenuId, corpId, filters]);




  // Handle Memorize - sends the exact payload to SaveMemorizedTimeAndAttendanceReport API
  const handleMemorize = useCallback(async () => {
    if (!permissions.canCreate && !permissions.canUpdate) {
      enqueueSnackbar("You don't have permission to memorize report.", {
        variant: 'error',
        anchorOrigin: { vertical: 'top', horizontal: 'center' },
      });
      return;
    }

    let deptName = filters?.departmentName;
    let jobName = filters?.jobTitleName;

    if ((!deptName || deptName === 'all') && reportData?.GridDetails && Array.isArray(reportData.GridDetails) && reportData.GridDetails.length > 0) {
      const match = reportData.GridDetails.find(
        (r) => (r.NimbleDeptID && r.NimbleDeptID === filters?.department) || (r.DepartmentID && r.DepartmentID === filters?.department)
      );
      if (match) {
        deptName = match.NimbleDeptName || match.DepartmentName;
      }
    }

    if ((!jobName || jobName === 'all') && reportData?.GridDetails && Array.isArray(reportData.GridDetails) && reportData.GridDetails.length > 0) {
      const match = reportData.GridDetails.find(
        (r) => (r.NimbleJobID && r.NimbleJobID === filters?.jobTitle) || (r.JobID && r.JobID === filters?.jobTitle)
      );
      if (match) {
        jobName = match.NimbleJobName || match.JobName;
      }
    }

    const payload = buildMemorizePayload({
      menuId: finalMenuId,
      transactionView: filters?.transactionView,
      reportType,
      reportTitle,
      selectedFields,
      department: filters?.department,
      departmentName: deptName,
      jobTitle: filters?.jobTitle,
      jobTitleName: jobName,
    });

    // console.log('--- MEMORIZE PAYLOAD ---');
    // console.log(payload);
    // console.log(JSON.stringify(payload, null, 2));

    setLoading(true);
    try {
      const res = await SaveMemorizedTimeAndAttendanceReport(payload);
      // console.log('--- MEMORIZE SAVE RESPONSE ---', res);
      if (res.StatusCode === 200) {
        enqueueSnackbar(res?.Status || res?.message || 'Report memorized successfully', {
          variant: 'success',
          anchorOrigin: { vertical: 'top', horizontal: 'center' },
        });
      }
      else {
        enqueueSnackbar(res?.Status || res?.message || 'Failed to memorize report', {
          variant: 'error',
          anchorOrigin: { vertical: 'top', horizontal: 'center' },
        });
      }
    } catch (error) {
      console.error('Error saving memorized report:', error);
      enqueueSnackbar('Failed to memorize report', {
        variant: 'error',
        anchorOrigin: { vertical: 'top', horizontal: 'center' },
      });
    } finally {
      setLoading(false);
    }
  }, [finalMenuId, filters, reportType, reportTitle, selectedFields, reportData, enqueueSnackbar, permissions.canCreate, permissions.canUpdate]);


  // Handle Reset - sends { MenuId } to TimeandAttendanceReportResetDetails API
  const handleReset = useCallback(async () => {
    if (!permissions.canDelete && !permissions.canUpdate) {
      enqueueSnackbar("You don't have permission to reset report.", {
        variant: 'error',
        anchorOrigin: { vertical: 'top', horizontal: 'center' },
      });
      return;
    }

    const payload = buildResetPayload(finalMenuId);
    // console.log('--- RESET PAYLOAD ---');
    // console.log(payload);
    // console.log(JSON.stringify(payload, null, 2));

    setLoading(true);
    try {
      const res = await ResetMemorizedTimeAndAttendanceReport(payload);
      // console.log('--- RESET RESPONSE ---', res);
      if (res.StatusCode === 200) {
        enqueueSnackbar(res?.Status || res?.message || 'Report reset successfully', {
          variant: 'success',
          anchorOrigin: { vertical: 'top', horizontal: 'center' },
        });
      } else {
        enqueueSnackbar(res?.Status || res?.message || 'Failed to reset report', {
          variant: 'error',
          anchorOrigin: { vertical: 'top', horizontal: 'center' },
        });
      }
    } catch (error) {
      console.error('Error resetting report:', error);
      enqueueSnackbar('Failed to reset report', {
        variant: 'error',
        anchorOrigin: { vertical: 'top', horizontal: 'center' },
      });
    } finally {
      setLoading(false);
    }
  }, [finalMenuId, enqueueSnackbar, permissions.canDelete, permissions.canUpdate]);

  const [loading, setLoading] = useState(false);

  // Handle Refresh - calls report API and logs generate payload to console
  const handleRefresh = useCallback(async () => {
    if (!permissions.canView) {
      enqueueSnackbar("You don't have permission to view report.", {
        variant: 'error',
        anchorOrigin: { vertical: 'top', horizontal: 'center' },
      });
      return;
    }

    setLoading(true);
    try {
      let currentFilters = { ...filters };

      if (
        (!currentFilters.allDepartmentIds || currentFilters.allDepartmentIds.length === 0) &&
        (!currentFilters.department || String(currentFilters.department).toLowerCase() === 'all')
      ) {
        try {
          const depts = await getDepartments();
          currentFilters.allDepartmentIds = (depts || []).map((d) => d?.value).filter(Boolean);
        } catch (e) {
          console.error('Error resolving departments for refresh payload:', e);
        }
      }

      if (
        (!currentFilters.allJobTitleIds || currentFilters.allJobTitleIds.length === 0) &&
        (!currentFilters.jobTitle || String(currentFilters.jobTitle).toLowerCase() === 'all')
      ) {
        try {
          const jobs = await getJobTitles();
          currentFilters.allJobTitleIds = (jobs || []).map((j) => j?.value).filter(Boolean);
        } catch (e) {
          console.error('Error resolving job titles for refresh payload:', e);
        }
      }

      const payload = buildGeneratePayload({
        filters: currentFilters,
        reportType,
        selectedFields,
        fallbackCorpId: corpId,
      });
      // console.log('--- REFRESH / GENERATE PAYLOAD ---');
      // console.log(payload);
      // console.log(JSON.stringify(payload, null, 2));

      const data = await GetTimeAndAttendanceReport(payload);
      // console.log('--- REFRESH REPORT API RESPONSE ---', data);
      setReportData(data);
    } catch (error) {
      console.error('Error refreshing Time and Attendance Report:', error);
    } finally {
      setLoading(false);
    }
  }, [filters, reportType, selectedFields, corpId, permissions.canView, enqueueSnackbar]);

  const initialFetchDoneRef = useRef(false);

  useEffect(() => {
    if (!reportData && !initialFetchDoneRef.current) {
      initialFetchDoneRef.current = true;
      handleRefresh();
    }
  }, [reportData, handleRefresh]);



  const handleCustomize = useCallback(() => {
    if (!permissions.canView) {
      enqueueSnackbar("You don't have permission to customize report.", {
        variant: 'error',
        anchorOrigin: { vertical: 'top', horizontal: 'center' },
      });
      return;
    }

    const targetUrl = getCustomizeNavigationUrl();
    navigate(targetUrl, {
      state: {
        ...(location.state || {}),
        filters,
        reportType,
        selectedFields,
        navigationTimestamp: Date.now(),
      },
    });
  }, [navigate, getCustomizeNavigationUrl, filters, reportType, selectedFields, location.state, permissions.canView, enqueueSnackbar]);


  const isMultiCorp = useMemo(() => {
    const rawGridDetails =
      reportData?.GridDetails && Array.isArray(reportData.GridDetails)
        ? reportData.GridDetails
        : Array.isArray(reportData)
          ? reportData
          : [];
    if (!rawGridDetails || rawGridDetails.length === 0) return false;
    const map = new Map();
    rawGridDetails.forEach((r) => {
      const name = r.CorporationName || r.corpName;
      const id = r.CorporationID || r.corpId;
      if (
        name !== null &&
        name !== undefined &&
        String(name).trim() !== '' &&
        String(name).toLowerCase() !== 'null' &&
        String(name).toLowerCase() !== 'undefined'
      ) {
        const key = String(id || name).trim();
        if (!map.has(key)) {
          map.set(key, String(name).trim());
        }
      } else if (
        id !== null &&
        id !== undefined &&
        String(id).trim() !== '' &&
        String(id).toLowerCase() !== 'null' &&
        String(id).toLowerCase() !== 'undefined'
      ) {
        const key = String(id).trim();
        if (!map.has(key)) {
          map.set(key, String(id).trim());
        }
      }
    });
    return map.size > 1;
  }, [reportData]);

  const selectedCorpCount = useMemo(() => {
    if (reportData?.GridDetails && Array.isArray(reportData.GridDetails) && reportData.GridDetails.length > 0) {
      const uniqueCorps = new Set(
        reportData.GridDetails.map((item) => item.CorporationName || item.CorporationID).filter(Boolean)
      );
      if (uniqueCorps.size > 0) return uniqueCorps.size;
    }
    return 1;
  }, [reportData]);

  const selectedPcCount = useMemo(() => {
    if (reportData?.GridDetails && Array.isArray(reportData.GridDetails) && reportData.GridDetails.length > 0) {
      const validPcs = reportData.GridDetails.filter((item) => {
        const pcId = item.PCID ?? item.pcId ?? item.ProfitCenterID;
        const pcName = item.PCName ?? item.pcName ?? item.ProfitCenterName;
        const validId =
          pcId !== null &&
          pcId !== undefined &&
          String(pcId).trim() !== '' &&
          String(pcId).toLowerCase() !== 'null' &&
          String(pcId).toLowerCase() !== 'undefined';
        const validName =
          pcName !== null &&
          pcName !== undefined &&
          String(pcName).trim() !== '' &&
          String(pcName).toLowerCase() !== 'null' &&
          String(pcName).toLowerCase() !== 'undefined';
        return validId || validName;
      }).map((item) => item.PCID || item.PCName);
      const uniquePcs = new Set(validPcs);
      return uniquePcs.size;
    }
    return 0;
  }, [reportData]);

  const pcNo = useMemo(() => {
    if (reportData?.GridDetails && Array.isArray(reportData.GridDetails) && reportData.GridDetails.length > 0) {
      const match = reportData.GridDetails.find((r) => {
        const val = r.PCNo ?? r.pcNo ?? r.PCNumber ?? r.ProfitCenterNo;
        return val !== undefined && val !== null && String(val).trim() !== '' && String(val).toLowerCase() !== 'null';
      });
      if (match) {
        return String(match.PCNo ?? match.pcNo ?? match.PCNumber ?? match.ProfitCenterNo).trim();
      }
    }
    return null;
  }, [reportData]);

  const handleExportPdf = useCallback(() => {
    if (!permissions.canView) {
      enqueueSnackbar("You don't have permission to export report.", {
        variant: 'error',
        anchorOrigin: { vertical: 'top', horizontal: 'center' },
      });
      return;
    }

    const rawGridDetails =
      reportData?.GridDetails && Array.isArray(reportData.GridDetails)
        ? reportData.GridDetails
        : [];
    if (!rawGridDetails || rawGridDetails.length === 0) {
      enqueueSnackbar('No data available to export', {
        variant: 'warning',
        anchorOrigin: { vertical: 'top', horizontal: 'center' },
      });
      return;
    }
    try {
      generateTimeAndAttendancePdf({
        filters,
        reportType,
        reportTitle,
        selectedFields,
        reportData,
        rawGridDetails,
        hasProfitCenter: rawGridDetails.some(
          (item) =>
            (item.PCID !== null &&
              item.PCID !== undefined &&
              String(item.PCID).trim() !== '' &&
              String(item.PCID).toLowerCase() !== 'null') ||
            (item.PCName !== null &&
              item.PCName !== undefined &&
              String(item.PCName).trim() !== '' &&
              String(item.PCName).toLowerCase() !== 'null')
        ),
        isMultiCorp,
        pcNo,
      });
      enqueueSnackbar('PDF generated successfully', {
        variant: 'success',
        anchorOrigin: { vertical: 'top', horizontal: 'center' },
      });
    } catch (err) {
      console.error('Error generating PDF:', err);
      enqueueSnackbar('Failed to generate PDF', {
        variant: 'error',
        anchorOrigin: { vertical: 'top', horizontal: 'center' },
      });
    }
  }, [
    filters,
    reportType,
    reportTitle,
    selectedFields,
    reportData,
    isMultiCorp,
    pcNo,
    enqueueSnackbar,
    permissions.canView,
  ]);

  const handleExportExcel = useCallback(() => {
    if (!permissions.canView) {
      enqueueSnackbar("You don't have permission to export report.", {
        variant: 'error',
        anchorOrigin: { vertical: 'top', horizontal: 'center' },
      });
      return;
    }

    const rawGridDetails =
      reportData?.GridDetails && Array.isArray(reportData.GridDetails)
        ? reportData.GridDetails
        : [];
    if (!rawGridDetails || rawGridDetails.length === 0) {
      enqueueSnackbar('No data available to export', {
        variant: 'warning',
        anchorOrigin: { vertical: 'top', horizontal: 'center' },
      });
      return;
    }
    try {
      generateTimeAndAttendanceExcel({
        filters,
        reportType,
        reportTitle,
        selectedFields,
        reportData,
        rawGridDetails,
        hasProfitCenter: rawGridDetails.some(
          (item) =>
            (item.PCID !== null &&
              item.PCID !== undefined &&
              String(item.PCID).trim() !== '' &&
              String(item.PCID).toLowerCase() !== 'null') ||
            (item.PCName !== null &&
              item.PCName !== undefined &&
              String(item.PCName).trim() !== '' &&
              String(item.PCName).toLowerCase() !== 'null')
        ),
        isMultiCorp,
        pcNo,
      });
      enqueueSnackbar('Excel file generated successfully', {
        variant: 'success',
        anchorOrigin: { vertical: 'top', horizontal: 'center' },
      });
    } catch (err) {
      console.error('Error generating Excel:', err);
      enqueueSnackbar('Failed to generate Excel file', {
        variant: 'error',
        anchorOrigin: { vertical: 'top', horizontal: 'center' },
      });
    }
  }, [
    filters,
    reportType,
    reportTitle,
    selectedFields,
    reportData,
    isMultiCorp,
    pcNo,
    enqueueSnackbar,
    permissions.canView,
  ]);

  // If state is missing, redirect back to the customize page
  if (!filters || !reportType) {
    const fallbackUrl = getCustomizeNavigationUrl() || '/TimeandAttendance';
    return <Navigate to={fallbackUrl} replace />;
  }

  return (
    <Box
      sx={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.default',
        p: 0,
        overflow: 'hidden',
      }}
    >
      {/* Fixed Toolbar */}
      <Box sx={{ flex: '0 0 auto', zIndex: 10, bgcolor: 'background.default' }}>
        <ReportToolbar
          onRefresh={handleRefresh}
          onMemorize={handleMemorize}
          onReset={handleReset}
          onExportExcel={handleExportExcel}
          onExportPdf={handleExportPdf}
          onCustomize={handleCustomize}
          permissions={permissions}
        />
      </Box>

      {/* Fixed Report Header */}
      <Box sx={{ flex: '0 0 auto', zIndex: 9, bgcolor: '#ffffff' }}>
        <ReportHeader
          corporation={filters.corporation === 'all' ? 'Multiple' : filters.corporation}
          reportTitle={reportTitle}
          fromDate={filters.fromDate}
          toDate={filters.toDate}
          isMultiCorp={isMultiCorp}
          selectedCorpCount={selectedCorpCount}
          selectedPcCount={selectedPcCount}
          pcNo={pcNo}
          reportType={reportType}
          reportData={reportData}
          filters={filters}
        />
      </Box>

      {/* Scrollable Table Area */}
      <Box sx={{ flex: '1 1 auto', overflow: 'hidden', minHeight: 0, p: 0, position: 'relative', display: 'flex', flexDirection: 'column', height: '100%' }}>
        {!permissions.canView ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              color: 'text.secondary',
              fontSize: '0.9rem',
              fontWeight: 500,
            }}
          >
            You don't have permission to view this report.
          </Box>
        ) : (
          <Tables
            filters={filters}
            reportType={reportType}
            selectedFields={selectedFields}
            reportData={reportData}
          />
        )}
      </Box>
      <Loader loading={loading || permissionsLoading} />
    </Box>
  );
};

export default LabourReportsPage;
